/* Sync / New Site (現場連携) admin API — wired to the backend BFF.
 *
 * A D-ORCA Site is D-ORCA-owned: its own name plus a required link to exactly
 * one Trusstor project and one Genba site. Sites are minted explicitly here;
 * the app does NOT mirror Trusstor — the trusstor-projects + genba-sites lists
 * survive only as pickers on this page.
 *
 * Endpoints (admin namespace):
 *   GET    /api/admin/genba-sites?keywords&status&limit&offset   (picker)
 *   GET    /api/admin/trusstor-projects                          (picker)
 *   GET    /api/admin/sites
 *   POST   /api/admin/sites          { name, trusstorProjectId, genbaSiteNumber }
 *   DELETE /api/admin/sites/:siteId
 *
 * The Genba side is mock-backed on the backend in Phase 1 (R-01); this module
 * is upstream-agnostic — it only speaks the d-orca contract.
 */

import type {
  IGenbaSitesPage,
  IGenbaSitesQuery,
  ISite,
  ITrusstorProject,
} from '@interfaces/IProjectMerge';
import type { IApiError } from '@interfaces/IApiError';
import type { ProviderName } from '@interfaces/IAuth';
import { authedFetch } from '@apis/auth';

/**
 * An upstream provider can't be reached for this tenant — the picker list is
 * unavailable until the user (re)connects. One typed error the page keys its
 * connect/reconnect pane off of (vs a generic fetch error that offers a retry):
 *
 *  - `not-connected`   — never connected (backend 409 `PROVIDER_NOT_CONNECTED`)
 *                        → "Connect".
 *  - `reauth-required` — connected but the session expired (backend 401
 *                        `PROVIDER_REAUTH_REQUIRED`). Genba has no refresh, so it
 *                        can only be repaired by reconnecting → "Reconnect".
 *  - `disconnected`    — a silent token refresh failed (backend 409
 *                        `PROVIDER_DISCONNECTED`, Trusstor) → "Reconnect".
 *
 * All recover via the provider's `/connect/:provider` form; `provider` + `state`
 * drive the project-merge reconnect pane copy.
 */
export type ProviderConnState = 'not-connected' | 'reauth-required' | 'disconnected';

export class ProviderConnectionError extends Error {
  constructor(
    readonly provider: ProviderName,
    readonly state: ProviderConnState,
  ) {
    super(`${provider} ${state}`);
    this.name = 'ProviderConnectionError';
  }
}

/** Narrow an unknown JSON value to IApiError. Only asserts structure, not values. */
function isApiError(v: unknown): v is IApiError {
  return typeof v === 'object' && v !== null && ('error' in v ? typeof (v as Record<string, unknown>).error === 'object' : true);
}

async function readEnvelope(res: Response): Promise<IApiError | null> {
  const raw: unknown = await res.json().catch(() => null);
  return isApiError(raw) ? raw : null;
}

export async function fetchGenbaSites(
  params: IGenbaSitesQuery = {},
  signal?: AbortSignal,
): Promise<IGenbaSitesPage> {
  const qs = new URLSearchParams();
  if (params.keywords) qs.set('keywords', params.keywords);
  if (params.status) qs.set('status', params.status);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';

  const res = await authedFetch(`/api/admin/genba-sites${suffix}`, { signal });
  if (res.ok) return (await res.json()) as IGenbaSitesPage;

  const body = await readEnvelope(res);
  const code = body?.error?.code;
  if (res.status === 409 && code === 'PROVIDER_NOT_CONNECTED') {
    throw new ProviderConnectionError('genba', 'not-connected');
  }
  // An expired Genba session: 401 PROVIDER_REAUTH_REQUIRED. (Gal's authedFetch
  // change hands provider-401s straight back without a wasteful d-orca-token
  // refresh+replay; until that lands, the refresh just no-ops and we still see
  // the 401 here.)
  if (res.status === 401 && code === 'PROVIDER_REAUTH_REQUIRED') {
    throw new ProviderConnectionError('genba', 'reauth-required');
  }
  throw new Error(`fetchGenbaSites failed: ${res.status}`);
}

export async function fetchTrusstorProjects(signal?: AbortSignal): Promise<ITrusstorProject[]> {
  const res = await authedFetch('/api/admin/trusstor-projects', { signal });
  if (res.ok) {
    const body = (await res.json()) as { projects: ITrusstorProject[] };
    return body.projects;
  }
  // Trusstor has a refresh token, so a dead session surfaces as a failed silent
  // refresh (PROVIDER_DISCONNECTED) rather than Genba's reauth-required; both
  // never-connected and disconnected come back as 409. Map them so the pane
  // prompts (re)connect instead of a generic fetch error.
  const body = await readEnvelope(res);
  const code = body?.error?.code;
  if (res.status === 409 && code === 'PROVIDER_NOT_CONNECTED') {
    throw new ProviderConnectionError('trusstor', 'not-connected');
  }
  if (res.status === 409 && code === 'PROVIDER_DISCONNECTED') {
    throw new ProviderConnectionError('trusstor', 'disconnected');
  }
  throw new Error(`fetchTrusstorProjects failed: ${res.status}`);
}

export async function fetchSites(signal?: AbortSignal): Promise<ISite[]> {
  const res = await authedFetch('/api/admin/sites', { signal });
  if (!res.ok) throw new Error(`fetchSites failed: ${res.status}`);
  const body = (await res.json()) as { sites: ISite[] };
  // `createdAt` stays the raw backend ISO string; the list formats it to a
  // short JST stamp at render (formatJstShort), keeping this layer free of
  // presentation concerns.
  return body.sites;
}

export async function createSite(
  name: string,
  trusstorProjectId: string,
  genbaSiteNumber: string,
): Promise<ISite> {
  const res = await authedFetch('/api/admin/sites', {
    method: 'POST',
    body: JSON.stringify({ name, trusstorProjectId, genbaSiteNumber }),
  });
  if (!res.ok) {
    const body = await readEnvelope(res);
    // 409 SITE_LINK_CONFLICT (a side already attached to another site) and
    // 404 NOT_FOUND (trusstor project not in the cached list) both surface the
    // code so callers can branch if they want.
    throw new Error(`createSite failed: ${body?.error?.code ?? res.status}`);
  }
  // `createdAt` is the raw backend ISO string — formatted at render, not here.
  return (await res.json()) as ISite;
}

export async function deleteSite(siteId: string): Promise<void> {
  const res = await authedFetch(`/api/admin/sites/${encodeURIComponent(siteId)}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(`deleteSite failed: ${res.status}`);
}
